import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from './portalToDropdown.module.scss';

type Props = {
	children: React.ReactNode
	containerRef: React.MutableRefObject<HTMLDivElement | null>
}

export const PortalToDropdown = ({ children, containerRef }: Props) => {
	const portalRef = useRef<HTMLDivElement>(null);
	const [style, setStyle] = useState<React.CSSProperties>();

	const calculateStyle = useCallback(
		() => {
			const container = containerRef.current;

			if (container) {
				const { bottom, top, width, left } = container.getBoundingClientRect();

				const viewportHeight = window.innerHeight;
				const spaceBelow = viewportHeight - bottom;
				const spaceAbove = top;
				const dropdownHeight = portalRef.current?.clientHeight || 0;
				const openAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
				const dropdownMaxWidth = 500;

				const dropdownStyle: React.CSSProperties = {
					minWidth: `${width}px`,
					maxWidth: `${dropdownMaxWidth}px`,
					left: `${left + window.scrollX}px`,
					...(openAbove ?
						{ bottom: `${viewportHeight - spaceAbove - window.scrollY}px` } :
						{ top: `${bottom + window.scrollY}px` }
					),
				};

				setStyle(dropdownStyle);
			}
		},
		[containerRef]
	)

	useLayoutEffect(
		() => {
			calculateStyle();

			window.addEventListener('scroll', calculateStyle, true);

			return () => {
				window.removeEventListener('scroll', calculateStyle, true);
			}
		},
		[calculateStyle]
	)

	const component = (
		<div ref={portalRef} className={styles.container} style={style}>
			{children}
		</div>
	)

	return createPortal(component, document.body);
}
